'use client';

import { ThemeVariables, variables as defaultVariables } from '../mpanels/variables';

const colors = {
    ...defaultVariables.colors,
    white: 'rgba(255, 255, 255, 1)',
    primary: {
        '100': 'rgba(210, 220, 221, 1)',
        '200': 'rgba(165, 185, 187, 1)',
        '300': 'rgba(119, 151, 154, 1)',
        '400': 'rgba(74, 116, 120, 1)',
        '500': 'rgba(29, 81, 86, 1)',
        '600': 'rgba(23, 65, 69, 1)',
        '700': 'rgba(17, 49, 52, 1)',
        '800': 'rgba(12, 32, 34, 1)',
        '900': 'rgba(6, 16, 17, 1)',
    },
    secondary: {
        '100': 'rgba(208, 228, 230, 1)',
        '200': 'rgba(160, 201, 205, 1)',
        '300': 'rgba(113, 175, 179, 1) ',
        '400': 'rgba(65, 148, 154, 1)',
        '500': 'rgba(18, 121, 129, 1)',
        '600': 'rgba(14, 97, 103, 1)',
        '700': 'rgba(11, 73, 77, 1)',
        '800': 'rgba(7, 48, 52, 1)',
        '900': 'rgba(4, 24, 26, 1)',
    },
    tertiary: {
        '100': 'rgba(207, 228, 238, 1)',
        '200': 'rgba(159, 200, 220, 1)',
        '300': 'rgba(110, 173, 203, 1)',
        '400': 'rgba(62, 145, 185, 1)',
        '500': 'rgba(14, 118, 168, 1)',
        '600': 'rgba(14, 118, 168, 1)',
        '700': 'rgba(8, 71, 101, 1)',
        '800': 'rgba(6, 47, 67, 1)',
        '900': 'rgba(3, 24, 34, 1)',
    },
    neutral: {
        '50': 'rgba(251, 251, 251, 1)',
        '100': 'rgba(245, 246, 247, 1)',
        '200': 'rgba(237, 237, 237, 1)',
        '300': 'rgba(228, 228, 228, 1)    ',
        '400': 'rgba(219, 219, 219, 1)',
        '500': 'rgba(210, 210, 210, 1)',
        '600': 'rgba(180, 180, 180, 1)',
        '700': 'rgba(151, 151, 151, 1)',
        '800': 'rgba(100, 100, 100, 1)',
        '900': 'rgba(74, 74, 74, 1)',
    },
    success: 'rgba(15, 119, 39, 1)',
    info: 'rgba(6, 87, 168, 1)',
    warning: 'rgba(242, 141, 0, 1)',
    error: 'rgba(142, 19, 32, 1)',
};

const palette = {
    ...defaultVariables.colors.palette,
    primary: {
        ...colors.primary,
        main: colors.primary['500'],
        contrastText: colors.white,
    },
    secondary: {
        ...colors.secondary,
        main: colors.secondary['500'],
        contrastText: colors.white,
    },
    tertiary: {
        ...colors.tertiary,
        main: colors.tertiary['500'],
        contrastText: colors.white,
    },
    neutral: colors.neutral,
    error: {
        main: colors.error,
    },
    background: {
        default: colors.white,
    },
    text: {
        primary: colors.neutral['800'],
    },
    info: {
        main: colors.info,
    },
};

const fonts = {
    primary: '"Inter", sans-serif',
    secondary: '"Inter", serif',
    additional: '"Inter", serif',
    body: {
        fontSize: '1rem',
    },
};

type SelectOpinionThemeVariables = ThemeVariables & {
    colors: {
        palette: ThemeVariables['colors']['palette'] & typeof palette;
    };
};

export const variables: SelectOpinionThemeVariables = {
    themeName: 'select-opinion',
    sortOrder: {
        ...defaultVariables.sortOrder,
    },
    colors: {
        palette,
        links: {
            main: colors.tertiary['500'],
            hover: colors.tertiary['400'],
            active: colors.tertiary['700'],
            disabled: colors.tertiary['500'],
        },
        error: {
            main: colors.error,
        },
        other: {
            ...defaultVariables.colors.other,
            icon: {
                ...defaultVariables.colors.other.icon,
                stroke: {
                    ...defaultVariables.colors.other.icon.stroke,
                    primary: colors.secondary['500'],
                },
            },
        },
    },
    fonts,
    borderRadius: {
        primary: '8px',
        secondary: '0',
        tertiary: '0',
        topMenu: '0',
        mobileTopMenu: '0 0 8px 8px',
    },
    padding: {
        ...defaultVariables.padding,
    },
    margin: {
        ...defaultVariables.margin,
    },
    boxShadows: {
        ...defaultVariables.boxShadows,
    },
    layout: {
        ...defaultVariables.layout,
    },
    cookie: {
        ...defaultVariables.cookie,
    },
    typography: {
        fontFamily: fonts.secondary,
        h1: {
            color: palette.primary.main,
            fontFamily: fonts.primary,
            fontSize: '2.375rem',
            fontWeight: '800',
        },
        h2: {
            color: palette.primary.main,
            fontFamily: fonts.primary,
            fontSize: '1.875rem',
            fontWeight: '800',
        },
        h3: {
            color: palette.primary.main,
            fontFamily: fonts.primary,
            fontSize: '1.5rem',
            fontWeight: '800',
        },
        h4: {
            color: palette.secondary.main,
            fontFamily: fonts.primary,
            fontSize: '1.25rem',
            fontWeight: '800',
        },
        h5: {
            color: palette.text.primary,
            fontFamily: fonts.primary,
            fontSize: '1.125rem',
        },
        h6: {
            color: palette.text.primary,
            fontFamily: fonts.primary,
            fontSize: '1rem',
            fontWeight: '800',
        },
        body1: {
            fontSize: '1rem',
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        body2: {
            fontSize: '0.875rem',
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        subtitle1: {
            fontSize: '1rem',
            fontFamily: fonts.secondary,
            color: palette.primary['600'],
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontFamily: fonts.secondary,
        },
        subtitle3: {
            fontSize: '0.75rem',
            fontFamily: fonts.secondary,
        },
        greyLabel: {
            fontSize: '0.75rem',
            fontFamily: fonts.secondary,
        },
        link: {
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
                color: colors.tertiary['400'],
            },
            '&:active': {
                textDecoration: 'inherit',
                color: colors.tertiary['600'],
            },
            '&:disabled': {
                textDecoration: 'inherit',
            },
        },
    },
};
